import { TPayloadList } from './types.ts';
import { dedupe } from '../cache.ts';
import { buildPayloadUrl } from '../gateway.ts';

const localeParams = (locale: string) => ({
  'locale':          locale,
  'fallback-locale': 'none',
});

const fetchDocs = async <TDoc>(url: URL): Promise<TDoc[]> => {
  const response = await fetch(url, { headers: { Accept: 'application/json' } });

  if (!response.ok) {
    throw new Error(`CMS request failed (${response.status}) for ${url.pathname}`);
  }

  const { docs, hasNextPage } = (await response.json()) as TPayloadList<TDoc>;

  if (hasNextPage) {
    console.error(
      `${url.pathname} returned more documents than the requested limit — `
      + 'raise it, the truncated documents are silently missing',
    );
  }

  return docs ?? [];
};

export type TFindParams = {
  locale?: string;
  depth?: number;
  limit?: number;
  sort?: string;
  select?: string[];
  populate?: Record<string, string[]>;
  where?: Record<string, string>;
};

const selectParams = (fields: string[]) =>
  Object.fromEntries(fields.map((field) => [`select[${field}]`, 'true']));

const populateParams = (populate: Record<string, string[]>) =>
  Object.fromEntries(
    Object.entries(populate).flatMap(([collection, fields]) =>
      fields.map((field) => [`populate[${collection}][${field}]`, 'true'])),
  );

export const findDocs = <TDoc>(
  collection: string,
  { locale, depth = 0, limit, sort, select, populate, where }: TFindParams = {},
): Promise<TDoc[]> => {
  const url = buildPayloadUrl(collection, {
    depth: String(depth),
    ...(limit !== undefined ? { limit: String(limit) } : {}),
    ...(sort ? { sort } : {}),
    ...(select ? selectParams(select) : {}),
    ...(populate ? populateParams(populate) : {}),
    ...(locale ? localeParams(locale) : {}),
    ...where,
  });

  return dedupe(url.toString(), () => fetchDocs<TDoc>(url));
};

export const findOne = async <TDoc>(
  collection: string,
  params: TFindParams = {},
): Promise<TDoc | undefined> => (await findDocs<TDoc>(collection, { ...params, limit: 1 }))[0];
